import { useState, useCallback } from 'react';

import { Col, Button } from 'react-bootstrap';

import DatePicker, { registerLocale } from 'react-datepicker';
import { useSelector } from 'react-redux';
import { RootState } from '@/ui/store/store';
import { translate } from '@/domain/utils/translate';

import es from 'date-fns/locale/es';
import en from 'date-fns/locale/en-US';

import Icon from '@/ui/assets/Icon';


interface DatePickerRangeProps {
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
}

/**
 * Componente `DatePickerRange` que permite seleccionar un rango de fechas.
 *
 * @param {DatePickerRangeProps} props - Propiedades del componente.
 * @param {function} props.onDateChange - Callback que se llama cuando cambia la fecha seleccionada.
 * @returns {JSX.Element} - Elemento JSX que representa el selector de rango de fechas.
 *
 * El componente utiliza el idioma español para la localización de las fechas.
 * Permite seleccionar un rango de fechas a partir de la fecha actual.
 * Muestra dos meses en el selector y permite navegar entre ellos.
 */
const DatePickerRange = ({ onDateChange }: DatePickerRangeProps): JSX.Element => {

  /** Seleccionamos el idioma desde Redux */
  const language = useSelector((state: RootState) => state.language.language);

  registerLocale(language, language === 'es' ? es : en);

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);

  //Callback para manejar el cambio de fechas en el componente DatePicker de rango de fechas
  const handleDateChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      onDateChange(start, end); // Llamar al callback con las fechas
    },
    [onDateChange],
  );

  return (
    <DatePicker
      locale={language}
      renderCustomHeader={({ monthDate, customHeaderCount, decreaseMonth, increaseMonth }) => (
        <Col>
          <Button
            aria-label={translate('sales.previous_month')}
            className='picker-navigation previous-button btn-light border border-1'
            style={customHeaderCount === 1 ? { visibility: 'hidden' } : undefined}
            onClick={decreaseMonth}>
            <Icon name='ico-left-arrow-small' classMain='bg-primary-1 svg-primary-4  btn-icon-arrow ' />
          </Button>
          <span className='picker-current-month paragraph'>
            {monthDate.toLocaleString(language, {
              month: 'long',
            })}
          </span>
          <Button
            aria-label={translate('sales.next_month')}
            className='picker-navigation next-button  border border-1'
            style={customHeaderCount === 0 ? { visibility: 'hidden' } : undefined}
            onClick={increaseMonth}>
            <Icon name='ico-right-arrow-small' classMain='bg-primary-1 svg-primary-4  btn-icon-arrow' />
          </Button>
        </Col>
      )}
      selected={startDate}
      startDate={startDate}
      endDate={endDate}
      onChange={handleDateChange}
      selectsRange
      monthsShown={2}
      inline
      minDate={new Date()}
    />
  );
};

export default DatePickerRange;
